import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Link } from '../components/Links';
import { fleet, Outcome, stock, TrackerDetail, TrackerRow, trackers, TrackerState, VehicleDetail } from '../api';
import { useAuth } from '../auth';
import { ActivityGrid } from '../components/ActivityGrid';
import { ConnectionTimeline } from '../components/ConnectionTimeline';
import { DeviceCharts } from '../components/DeviceCharts';
import { RawLog } from '../components/RawLog';
import { Commands } from '../components/Commands';
import { Flag } from '../components/Flag';
import { icons } from '../components/Icons';
import { JourneyRules } from '../components/JourneySettings';
import { InputMapping } from '../components/InputMapping';
import { BackState, Diagnostic, Panel, RemoveForm, TrackerPanel, VehiclePanel } from '../components/Panels';
import { Temperatures } from '../components/Temperatures';
import { Products } from '../components/Products';
import { Attachments } from '../components/Attachments';
import { GeofenceVisits } from '../components/GeofenceVisits';
import { Timeline } from '../components/Timeline';
import { UnitStatusPill } from '../components/UnitStatusPill';
import { SubscriptionBox } from '../components/Subscription';
import { ago, dateTime, STATE_LABEL, TYPE_LABEL } from '../format';

// Interface refresh, screen 5: a header with the key facts, a sticky bar that jumps to each box,
// then the boxes in three columns (stacked on phones).
// One screen for a unit: a vehicle, its fitted tracker, or both. /vehicles/:id shows the
// vehicle and whatever tracker is fitted; /trackers/:id shows a tracker that isn't fitted
// (a fitted one goes to its vehicle's screen). Boxes for the missing half are left out.
// The boxes the jump bar offers, in reading order; only those on the page are shown.
const SECTIONS: Array<[label: string, box: string]> = [
  ['Vehicle', 'Vehicle'], ['Device', 'Device & SIM'], ['Self-install', 'Self-install'], ['Subscription', 'Subscription'],
  ['Installation', 'Installation'], ['Temperatures', 'Temperatures'], ['Diagnostic', 'Diagnostic'], ['Activity', 'Data activity'],
  ['Connections', 'Connection timeline'], ['Charts', 'Charts'], ['Inputs', 'Inputs and live values'], ['Commands', 'Commands'], ['Raw log', 'Raw data log'], ['Rules', 'Journey rules'], ['Geofences', 'Geofence visits'], ['Vehicle timeline', 'Vehicle timeline'], ['Device timeline', 'Device timeline'],
  ['Products', 'Installed products'], ['Vehicle files', 'Vehicle files'], ['Device files', 'Device files'],
];

function JumpBar({ deps }: { deps: unknown }) {
  const [present, setPresent] = useState<typeof SECTIONS>([]);
  const ref = useRef<HTMLElement>(null);
  // Some boxes appear after their data loads (e.g. Temperatures), so keep watching the page.
  useEffect(() => {
    const page = ref.current?.closest('.page');
    if (!page) return;
    let timer = 0;
    const check = () => {
      const now = SECTIONS.filter(([, box]) => page.querySelector(`[aria-label="${box}"]`));
      setPresent((was) => (was.length === now.length && was.every((x, i) => x === now[i]) ? was : now));
    };
    check();
    const watch = new MutationObserver(() => { window.clearTimeout(timer); timer = window.setTimeout(check, 150); });
    watch.observe(page, { childList: true, subtree: true });
    return () => { watch.disconnect(); window.clearTimeout(timer); };
  }, [deps]);
  const jump = (box: string) => {
    const page = ref.current?.closest<HTMLElement>('.page');
    const el = page?.querySelector<HTMLElement>(`[aria-label="${box}"]`);
    if (!page || !el) return;
    // Scroll the page only (scrollIntoView would also shift the app frame at the end of the page).
    const top = page.scrollTop + el.getBoundingClientRect().top - page.getBoundingClientRect().top - (ref.current?.offsetHeight ?? 0) - 8;
    page.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    el.tabIndex = -1;
    el.focus({ preventScroll: true });
  };
  return (
    <nav className="jumpbar" aria-label="Jump to" ref={ref}>
      {present.map(([label, box]) => <button key={box} type="button" onClick={() => jump(box)}>{label}</button>)}
    </nav>
  );
}

export function UnitPage({ from }: { from: 'vehicle' | 'tracker' }) {
  const id = Number(useParams().id);
  const { user } = useAuth();
  const [search] = useSearchParams();
  const location = useLocation();
  const back = (location.state ?? {}) as BackState;
  const [vehicle, setVehicle] = useState<VehicleDetail | null>(null);
  const [tracker, setTracker] = useState<TrackerDetail | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    try {
      if (from === 'vehicle') {
        const v = await fleet.vehicle(id);
        const t = v.tracker ? await trackers.get(v.tracker.id) : null;
        setVehicle(v);
        setTracker(t);
      } else {
        setTracker(await trackers.get(id));
        setVehicle(null);
      }
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
    setLoaded(true);
  }, [from, id]);

  useEffect(() => {
    setLoaded(false);
    setVehicle(null);
    setTracker(null);
    setRemoving(false);
    setNotice('');
    void load();
  }, [load]);

  // Rules, mapping and installation changes recalculate journeys in the background.
  useEffect(() => {
    if (!vehicle?.recalculating) return;
    const timer = setTimeout(() => void load(), 3000);
    return () => clearTimeout(timer);
  }, [vehicle, load]);

  const saved = (msg: string) => {
    setNotice(msg);
    void load();
  };

  if (error && !loaded) return <div className="page"><p className="error">{error}</p></div>;
  if (!loaded || (!vehicle && !tracker)) {
    return <div className="page">{error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>}</div>;
  }

  // A fitted tracker belongs on its vehicle's screen.
  const fittedIn = from === 'tracker' ? tracker?.tracker.vehicle : null;
  if (fittedIn) return <Navigate to={`/vehicles/${fittedIn.id}`} state={location.state} replace />;

  const v = vehicle?.vehicle ?? null;
  const t = tracker?.tracker ?? null;
  const here = v ? { from: `/vehicles/${v.id}`, fromLabel: v.name } : { from: `/trackers/${t!.id}`, fromLabel: `Tracker ${t!.imei}` };
  const position = vehicle?.position ?? tracker?.position ?? null;
  const defaultBack = v ? { to: '/vehicles', label: 'Vehicles' } : { to: '/trackers', label: 'Trackers' };

  return (
    <div className="page wide unit-page">
      <div className="unit-inner">
      <p><Link to={back.from ?? defaultBack.to}>← {back.fromLabel ?? defaultBack.label}</Link></p>

      <header className="card unit-head">
        <div className="unit-id">
          <span className="unit-icon" aria-hidden="true">{v ? icons.truck : icons.tracker}</span>
          <div className="unit-title">
            {v ? (
              <>
                <h1>{v.name}</h1>
                <p className="muted">
                  {[v.registration && v.registration !== v.name ? v.registration : null, TYPE_LABEL[v.type]].filter(Boolean).join(' · ')}
                  {v.customer && <> · <Link to={`/customers/${v.customer.id}`}>{v.customer.name}</Link></>}
                </p>
              </>
            ) : (
              <>
                <h1 className="mono">{t!.imei}</h1>
                <p className="muted">{[t!.model ?? 'Tracker', STATE_LABEL[t!.state]].join(' · ')} · not fitted to a vehicle</p>
              </>
            )}
          </div>
        </div>
        <div className="unit-side">
          {tracker && (
            <div className="unit-state">
              <UnitStatusPill status={tracker.status} />
              {t!.lastReceived && <span className="small">Last data <strong>{ago(t!.lastReceived)}</strong></span>}
            </div>
          )}
          <nav className="unit-links" aria-label="Unit">
            {v && position && <Link className="button small" to={`/?vehicle=${v.id}`}>Live position</Link>}
            {v && <Link className="button ghost small" to={`/vehicles/${v.id}/journeys`}>Journeys</Link>}
            {!v && t!.state === 'stock' && <Link className="button small" to={`/install?tracker=${t!.id}`}>Install in a vehicle</Link>}
          </nav>
        </div>
        <dl className="unit-facts">
          {t ? (
            <>
              {v && <div><dt>Tracker</dt><dd className="mono">{t.imei}</dd></div>}
              <div><dt>Model</dt><dd>{t.model ?? <span className="muted">not set</span>}</dd></div>
              <div><dt>Last data</dt><dd>{t.lastReceived ? dateTime(t.lastReceived) : '–'}</dd></div>
              <div><dt>Network</dt><dd>{tracker!.operator
                ? <>{tracker!.operator.country && <Flag country={tracker!.operator.country} name={tracker!.operator.countryName ?? ''} />} {tracker!.operator.name ?? tracker!.operator.code}</>
                : '–'}</dd></div>
              <div><dt>Last 24 h</dt><dd>{tracker!.recent.points.toLocaleString('en-IE')} records{tracker!.recent.late ? `, ${tracker!.recent.late} late` : ''}</dd></div>
              {v && vehicle!.tracker && <div><dt>Installed</dt><dd>{dateTime(vehicle!.tracker.installedAt)}</dd></div>}
            </>
          ) : (
            <div><dt>Tracker</dt><dd className="muted">No tracker fitted</dd></div>
          )}
        </dl>
      </header>
      <JumpBar deps={`${loaded}|${!!vehicle}|${!!tracker}|${t?.selfInstall}|${t?.activatedAt}`} />

      {search.get('installed') && v && (
        <div className="callout">
          <strong>Tracker installed.</strong>
          <p>If it isn't reporting yet, set its <b>second server</b> to <span className="mono">{vehicle!.trackerServer}</span> (TCP), in <b>Duplicate</b> mode. Leave its main server unchanged.</p>
        </div>
      )}
      {vehicle?.recalculating && <p className="notice" role="status">Recalculating journeys…</p>}
      {!vehicle?.recalculating && notice && <p className="notice" role="status">{notice}</p>}
      {error && <p className="error" role="alert">{error}</p>}

      <div className="unit-cols">
        <div className="unit-col">
          {v && <VehiclePanel vehicle={v} onSaved={() => saved('Vehicle details saved.')} />}
          {t && <TrackerPanel tracker={t} onSaved={() => saved('Tracker details saved.')} />}
          {t && (t.selfInstall || (user?.role === 'admin' && ['stock', 'allocated', 'shipped'].includes(t.state))) && (
            <SelfInstallPanel tracker={t} admin={user?.role === 'admin'} onSaved={saved} />
          )}
          {t?.selfInstall && t.activatedAt && <SubscriptionBox key={`sub-${t.id}`} trackerId={t.id} onSaved={saved} />}
          <Panel title="Installation" editing={false} actions={v && t && !removing && (
            <span className="unit-links">
              <Link to={`/install?vehicle=${v.id}`}>Swap</Link>
              <button className="link danger" onClick={() => setRemoving(true)}>Remove</button>
            </span>
          )}>
            {v && t && (
              <>
                {removing && (
                  <RemoveForm installationId={vehicle!.tracker!.installationId} imei={t.imei} vehicleName={v.name}
                    onCancel={() => setRemoving(false)} onDone={() => { setRemoving(false); saved('Tracker removed.'); }} />
                )}
                <dl className="facts">
                  <dt>Tracker</dt><dd className="mono">{t.imei}</dd>
                  <dt>Installed</dt><dd>{dateTime(vehicle!.tracker!.installedAt)}</dd>
                </dl>
              </>
            )}
            {v && !t && (
              <>
                <p className="muted">No tracker is fitted to this vehicle.</p>
                <div className="actions"><Link className="button" to={`/install?vehicle=${v.id}`}>Install a tracker</Link></div>
              </>
            )}
            {!v && t && <StateForm id={t.id} state={t.state} onSaved={saved} />}
            <InstallHistory vehicle={vehicle} tracker={tracker} here={here} />
          </Panel>
          {vehicle && <JourneyRules data={vehicle} canEdit={user?.role === 'admin'} onSaved={saved} />}
          {vehicle && <GeofenceVisits vehicleId={v!.id} />}
          {/* No tracker, so no middle column: the inputs box goes here. */}
          {vehicle && !tracker && <Temperatures vehicleId={v!.id} />}
          {vehicle && !tracker && <InputMapping vehicle={vehicle} tracker={null} canEdit={user?.role === 'admin'} onSaved={saved} />}
        </div>
        {tracker && (
          <div className="unit-col">
            {vehicle && <Temperatures vehicleId={v!.id} />}
            <Diagnostic checks={tracker.checks} lastReceived={t!.lastReceived} lastFix={t!.lastFix} operator={tracker.operator} recent={tracker.recent} />
            <ActivityGrid trackerId={t!.id} />
            <ConnectionTimeline trackerId={t!.id} />
            <DeviceCharts trackerId={t!.id} />
            {/* Inputs and live values: mapping needs a vehicle; an unfitted tracker shows live values only. */}
            <InputMapping vehicle={vehicle} tracker={{ id: t!.id, model: t!.model }} canEdit={!!vehicle && user?.role === 'admin'} onSaved={saved} />
            <Commands trackerId={t!.id} imei={t!.imei} label={v ? v.name : `tracker ${t!.imei}`} />
            <RawLog trackerId={t!.id} />
          </div>
        )}
        <div className="unit-col">
          {vehicle && (
            <Timeline subject="vehicles" id={v!.id} title="Vehicle timeline" placeholder="Comment on the vehicle"
              entries={vehicle.timeline} onChange={() => void load()} />
          )}
          {tracker && (
            <Timeline subject="trackers" id={t!.id} title="Device timeline" placeholder={'Comment on the tracker, e.g. "Swapped SIM"'}
              entries={tracker.timeline} onChange={() => void load()} />
          )}
          {vehicle && <Products vehicleId={v!.id} onChanged={() => void load()} />}
          {vehicle && <Attachments subject="vehicles" id={v!.id} title="Vehicle files" />}
          {tracker && <Attachments subject="trackers" id={t!.id} title="Device files" />}
        </div>
      </div>
      </div>
    </div>
  );
}

// Earlier fittings: the vehicle's trackers, and (for a tracker) the vehicles it was in.
function InstallHistory({ vehicle, tracker, here }: { vehicle: VehicleDetail | null; tracker: TrackerDetail | null; here: BackState }) {
  const rows = [
    ...(vehicle?.installations ?? []).map((i) => ({ ...i, link: `/trackers/${i.tracker.id}`, label: i.tracker.imei, mono: true })),
    ...(tracker?.installations ?? [])
      .filter((i) => !vehicle?.installations.some((x) => x.id === i.id))
      .map((i) => ({ ...i, link: `/vehicles/${i.vehicle.id}`, label: i.vehicle.name, mono: false })),
  ].sort((a, b) => b.installedAt.localeCompare(a.installedAt));
  if (rows.length === 0) return null;
  return (
    <details>
      <summary className="small">Installation history ({rows.length})</summary>
      <ul className="small">
        {rows.map((i) => (
          <li key={i.id}>
            <Link className={i.mono ? 'mono' : undefined} to={i.link} state={here}>{i.label}</Link>:{' '}
            {dateTime(i.installedAt)} – {i.removedAt ? dateTime(i.removedAt) : 'now'}
            {i.notes && <span className="muted"> · {i.notes}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}

// A tracker that isn't installed: in stock, faulty or retired.
function StateForm({ id, state, onSaved }: { id: number; state: TrackerState; onSaved: (msg: string) => void }) {
  const [value, setValue] = useState<Outcome>(state === 'stock' || state === 'faulty' || state === 'retired' ? state : 'stock');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await trackers.save(id, { state: value });
      onSaved('State saved.');
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };
  return (
    <form className="form" onSubmit={submit}>
      <p className="muted">Not installed in a vehicle{state === 'allocated' ? '; reserved for an order' : state === 'shipped' ? '; shipped to a customer' : ''}.</p>
      <div className="actions">
        <label className="sr-only" htmlFor="t-state">State</label>
        <select id="t-state" value={value} onChange={(e) => setValue(e.target.value as Outcome)}>
          <option value="stock">In stock</option><option value="faulty">Faulty</option><option value="retired">Retired</option>
        </select>
        <button className="primary" disabled={busy || value === state}>Save</button>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
    </form>
  );
}

// Self-install: claim code, labels, order. Admins can also make a stock tracker a self-install
// unit or give it new codes (the bulk versions are on the Trackers list).
function SelfInstallPanel({ tracker: t, admin, onSaved }: { tracker: TrackerRow; admin: boolean; onSaved: (msg: string) => void }) {
  const [code, setCode] = useState<{ claimCode: string; link: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const act = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      onSaved(msg);  // also reloads the timeline (showing a code or printing is noted there)
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };
  const codes = (action: 'prepare' | 'renew' | 'undo') => {
    const ask = {
      prepare: 'Make this tracker a self-install unit? It gets a claim code and QR code for its label.',
      renew: 'Make a NEW claim code and QR code? The label and box insert printed before stop working, so reprint them.',
      undo: 'Make this an ordinary tracker again? Its claim code and QR code stop working.',
    }[action];
    if (!window.confirm(ask)) return;
    setCode(null);
    void act(async () => {
      const r = (await stock.selfInstall([t.id], action)).results[0];
      if (r?.error) throw new Error(`Not done: ${r.error}.`);
    }, { prepare: 'Self-install unit: claim code made.', renew: 'New claim code made.', undo: 'No longer a self-install unit.' }[action]);
  };
  const size = (() => {
    try { const v = JSON.parse(localStorage.getItem('sprout.labelSize') ?? ''); if (v.widthMm > 0) return v; } catch { /* default */ }
    return { widthMm: 62, heightMm: 32 };
  })();
  const editable = !t.activatedAt && ['stock', 'allocated', 'shipped'].includes(t.state);

  return (
    <Panel title="Self-install" editing={false}>
      {!t.selfInstall ? (
        <>
          <p className="muted small">Not a self-install unit. A self-install unit gets a claim code and QR code, printed on its label, so the customer can activate it themselves.</p>
          <div className="actions"><button disabled={busy} onClick={() => codes('prepare')}>Make self-install unit</button></div>
        </>
      ) : (
        <>
          <dl className="facts">
            <dt>Claim code</dt>
            <dd>{code ? <span className="code-big">{code.claimCode.slice(0, 4)}-{code.claimCode.slice(4)}</span>
              : t.hasClaimCode ? <button className="link" disabled={busy} onClick={() => void act(async () => setCode(await stock.claimCode(t.id)), '')}>Show</button>
              : <span className="muted">none</span>}</dd>
            {code && <><dt>QR link</dt><dd className="small mono">{code.link}</dd></>}
            {t.codesCreatedAt && <><dt>Codes made</dt><dd>{dateTime(t.codesCreatedAt)}</dd></>}
            {(t.orderRef || t.orderEmail) && <><dt>Order</dt><dd>{[t.orderRef, t.orderEmail].filter(Boolean).join(' · ')}</dd></>}
            {t.allocatedAt && <><dt>Reserved</dt><dd>{dateTime(t.allocatedAt)}</dd></>}
            {t.shippedAt && <><dt>Shipped</dt><dd>{dateTime(t.shippedAt)}</dd></>}
            <dt>Activated</dt><dd>{t.activatedAt ? dateTime(t.activatedAt) : <span className="muted">not yet</span>}</dd>
            {t.activatedAt && <><dt>First data</dt><dd>{t.firstDataAt ? dateTime(t.firstDataAt) : <span className="muted">not yet</span>}</dd></>}
            {t.paidUntil && <><dt>Tracking until</dt><dd>{dateTime(t.paidUntil)}</dd></>}
          </dl>
          {code && <p className="muted small">Showing the code is noted on the timeline.</p>}
          {t.hasClaimCode && (
            <div className="actions">
              <button disabled={busy} onClick={() => void act(() => stock.print([t.id], 'label', size), 'Label downloaded as a PDF.')}>Print label</button>
              <button disabled={busy} onClick={() => void act(() => stock.print([t.id], 'insert'), 'Box insert downloaded as a PDF.')}>Print box insert</button>
            </div>
          )}
          {admin && editable && (
            <div className="actions small">
              <button className="link" disabled={busy} onClick={() => codes('renew')}>New claim code</button>
              <button className="link danger" disabled={busy} onClick={() => codes('undo')}>No longer self-install</button>
            </div>
          )}
        </>
      )}
      {error && <p className="error" role="alert">{error}</p>}
    </Panel>
  );
}
